import React, { useState, useEffect, useMemo } from 'react';
import { Vehicule, Intervention, Batiment } from '../../../../shared/types/game';
import { VehiculeConfig, FonctionArmement, Competence, PersonnelConfig } from '../../../../shared/types/config';
import { getApiBaseUrl } from '../../../../core/config/api';
import { getServiceIdFromBuildingType } from '../../utils/buildingServiceMapping';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import { updateUser } from '../../../../core/store/slices/userSlice';
import { loadVehiculeDetails } from './loadVehiculeDetails';
import VehiculeCrewSection from './VehiculeCrewSection';

interface BuildingVehiculesTabProps {
  vehicules: Vehicule[];
  loadingVehicules: boolean;
  buildingType: string;
  buildingId: number;
  userId: number;
  buildingCapacity?: number;
  allBuildings?: Batiment[];
  onVehiculeAdded?: () => void;
}

const BuildingVehiculesTab: React.FC<BuildingVehiculesTabProps> = ({
  vehicules,
  loadingVehicules,
  buildingType,
  buildingId,
  userId,
  buildingCapacity = 0,
  allBuildings = [],
  onVehiculeAdded
}) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const [showAddModal, setShowAddModal] = useState(false);
  const [vehiculeConfigs, setVehiculeConfigs] = useState<VehiculeConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [showVehiculeDetailsModal, setShowVehiculeDetailsModal] = useState(false);
  const [selectedVehicule, setSelectedVehicule] = useState<Vehicule | null>(null);
  const [fonctionsArmement, setFonctionsArmement] = useState<FonctionArmement[]>([]);
  const [competences, setCompetences] = useState<Competence[]>([]);
  const [personnelConfigs, setPersonnelConfigs] = useState<PersonnelConfig[]>([]);
  const [vehiculeConfigDetails, setVehiculeConfigDetails] = useState<VehiculeConfig | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [vehiculeSearchQuery, setVehiculeSearchQuery] = useState('');
  const [vehiculeFilterStatut, setVehiculeFilterStatut] = useState<string>('all'); // 'all', 'disponible', 'en_mission', 'en_maintenance', 'panne'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [viewMode, setViewMode] = useState<'compact' | 'detailed'>('compact');
  const [interventions, setInterventions] = useState<Intervention[]>([]);

  // Obtenir l'ID du service correspondant au type de bâtiment
  const serviceId = getServiceIdFromBuildingType(buildingType);

  // Charger les interventions pour calculer le nombre d'interventions par véhicule
  useEffect(() => {
    const loadInterventions = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }

        const response = await fetch(`${getApiBaseUrl(true)}/buildings/interventions`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            setInterventions(data.data);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des interventions:', error);
      }
    };

    loadInterventions();
  }, []);

  // Calculer le nombre d'interventions par véhicule
  const interventionsParVehicule = useMemo(() => {
    const countMap = new Map<number, number>();
    
    interventions.forEach((intervention) => {
      if (intervention.vehicules_ids && Array.isArray(intervention.vehicules_ids)) {
        intervention.vehicules_ids.forEach((vehiculeId) => {
          const currentCount = countMap.get(vehiculeId) || 0;
          countMap.set(vehiculeId, currentCount + 1);
        });
      }
    });
    
    return countMap;
  }, [interventions]);

  // Filtrer les véhicules avec useMemo pour optimiser les performances
  const filteredVehicules = useMemo(() => {
    return vehicules.filter((v) => {
      // Filtre par recherche (nom, type)
      const searchLower = vehiculeSearchQuery.toLowerCase().trim();
      if (searchLower && !(
        v.nom.toLowerCase().includes(searchLower) ||
        v.type.toLowerCase().includes(searchLower)
      )) {
        return false;
      }

      // Filtre par statut
      if (vehiculeFilterStatut !== 'all' && v.statut !== vehiculeFilterStatut) {
        return false;
      }

      return true;
    });
  }, [vehicules, vehiculeSearchQuery, vehiculeFilterStatut]);

  // Pagination
  const totalPages = Math.ceil(filteredVehicules.length / itemsPerPage);
  const paginatedVehicules = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredVehicules.slice(startIndex, endIndex);
  }, [filteredVehicules, currentPage, itemsPerPage]);

  // Réinitialiser la page quand le filtre change
  useEffect(() => {
    setCurrentPage(1);
  }, [vehiculeSearchQuery, vehiculeFilterStatut]);

  // Charger les configurations de véhicules filtrées par service
  useEffect(() => {
    const loadVehiculeConfigs = async () => {
      if (!serviceId) return;

      setLoadingConfigs(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${getApiBaseUrl(true)}/config/vehicules`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            // Filtrer par service
            const filteredConfigs = data.data.filter((config: VehiculeConfig) => 
              config.id_service === serviceId
            );
            setVehiculeConfigs(filteredConfigs);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des configurations de véhicules:', error);
      } finally {
        setLoadingConfigs(false);
      }
    };

    // Charger les configurations au montage du composant pour afficher les capacités
    loadVehiculeConfigs();
  }, [serviceId]);

  const handleOpenVehiculeDetails = async (vehicule: Vehicule) => {
    setSelectedVehicule(vehicule);
    setShowVehiculeDetailsModal(true);
    setLoadingDetails(true);
    setVehiculeConfigDetails(null);
    setPersonnelConfigs([]);

    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingDetails(false);
      return;
    }

    try {
      const r = await loadVehiculeDetails(vehicule, token);
      setVehiculeConfigDetails(r.vehiculeConfigDetails);
      setFonctionsArmement(r.fonctionsArmement);
      setCompetences(r.competences);
      setPersonnelConfigs(r.personnelConfigs);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSaveNom = async (nom: string) => {
    if (!selectedVehicule) {
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/${selectedVehicule.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ nom })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Mettre à jour le véhicule dans la liste locale
        setSelectedVehicule({ ...selectedVehicule, nom });
        // Recharger la liste des véhicules
        if (onVehiculeAdded) {
          onVehiculeAdded();
        }
      } else {
        alert(data.error || 'Erreur lors de la sauvegarde du nom');
        throw new Error(data.error || 'Erreur lors de la sauvegarde du nom');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du nom:', error);
      alert('Erreur lors de la sauvegarde du nom');
      throw error;
    }
  };

  const handleDeleteVehicule = async (vehiculeId: number) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Session expirée. Reconnectez-vous.');
      return;
    }
    const response = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/${vehiculeId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      alert((data as { error?: string }).error || 'Suppression impossible.');
      return;
    }
    if (onVehiculeAdded) {
      onVehiculeAdded();
    }
  };

  const getVehiculeConfig = (vehicule: Vehicule): VehiculeConfig | null => {
    return (
      vehiculeConfigs.find(
        (config) =>
          vehicule.config_vehicle_id != null &&
          config.id != null &&
          config.id === vehicule.config_vehicle_id
      ) ||
      vehiculeConfigs.find(
        (config) => config.nom === vehicule.nom || config.type === vehicule.type
      ) ||
      null
    );
  };

  // VehiculeCard component extracted to ./VehiculeCard.tsx


  // Fonction pour créer un ou plusieurs véhicules
  const handleCreateVehicule = async (config: VehiculeConfig, quantity: number = 1) => {
    if (!currentUser || isCreating) return;

    setIsCreating(true);
    setCreateError(null);
    setCreateSuccess(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setCreateError('Non authentifié');
        return;
      }

      const response = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          configId: config.id,
          batimentId: buildingId,
          quantity,
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setCreateError(data.error || 'Erreur lors de la création du véhicule');
        return;
      }

      if (data.data && data.data.newMoney !== undefined && currentUser) {
        dispatch(updateUser({ argents: data.data.newMoney }));
      }

      const count = data.data?.count ?? quantity;
      setCreateSuccess(data.message || `${count} véhicule${count > 1 ? 's' : ''} créé${count > 1 ? 's' : ''} avec succès`);
      onVehiculeAdded?.();
      // Signal global (écouté notamment par le tutoriel).
      window.dispatchEvent(new CustomEvent('opsecours:vehicle-purchased'));
      setTimeout(() => {
        setCreateSuccess(null);
      }, 2000);
    } catch (error: any) {
      console.error('Erreur lors de la création du véhicule:', error);
      setCreateError('Erreur réseau lors de la création du véhicule');
    } finally {
      setIsCreating(false);
    }
  };

  const [buyConfigId, setBuyConfigId] = useState<string>('');
  const [buyQty, setBuyQty] = useState<number>(1);
  const [transferTarget, setTransferTarget] = useState<string>('');
  const [transferring, setTransferring] = useState(false);

  const casernesCibles = allBuildings.filter(
    (b) => String(b.type).toLowerCase() !== 'cta' && Number(b.id) !== Number(buildingId),
  );

  const doTransfer = async (vehiculeId: number) => {
    if (!transferTarget) return;
    setTransferring(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/${vehiculeId}/transfer`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ batiment_id: Number(transferTarget) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        alert(data.error || 'Transfert impossible.');
        return;
      }
      setTransferTarget('');
      setSelectedVehicule(null);
      onVehiculeAdded?.();
    } catch {
      alert('Erreur réseau lors du transfert.');
    } finally {
      setTransferring(false);
    }
  };

  const scOf = (statut: string): [string, string] =>
    (({
      disponible: ['#22B14C', '#000000'],
      en_mission: ['#F7941D', '#000000'],
      en_maintenance: ['#3A3A3A', '#22B14C'],
      panne: ['#3A3A3A', '#FF0000'],
    } as Record<string, [string, string]>)[String(statut)] || ['#3A3A3A', '#FFFFFF']);

  const statutLabel = (statut: string): string =>
    ({
      disponible: 'Disponible',
      en_mission: 'En mission',
      en_maintenance: 'Maintenance',
      panne: 'En panne',
    } as Record<string, string>)[String(statut)] || String(statut);

  const doBuy = () => {
    const cfg = vehiculeConfigs.find((c) => String(c.id) === buyConfigId);
    if (cfg) handleCreateVehicule(cfg, buyQty);
  };

  const capV = buildingCapacity;

  const ficheEngin = (v: Vehicule) => {
    const cfg = getVehiculeConfig(v);
    const [bg, fg] = scOf(v.statut);
    return (
      <>
        <div className="stats4">
          <div className="stat"><b>{interventionsParVehicule.get(v.id) || 0}</b><span>interventions</span></div>
          <div className="stat"><b>{Math.round(Number(v.kilometrage || 0)).toLocaleString('fr-FR')}</b><span>km</span></div>
          <div className="stat"><b>{v.capacite ?? cfg?.niveaux ?? '—'}</b><span>équipage max</span></div>
          <div className="stat"><b>{cfg ? cfg.prix.toLocaleString('fr-FR') : '—'}</b><span>valeur (€)</span></div>
        </div>
        <dl className="kv" style={{ marginTop: 8 }}>
          <dt>Type</dt><dd>{v.type || '—'}{cfg?.nom ? <span className="mono muted"> · {cfg.nom}</span> : null}</dd>
          <dt>Statut</dt><dd><span className="pbadge" style={{ background: bg, color: fg }}>{statutLabel(v.statut)}</span></dd>
          <dt>Équipage</dt><dd className="mono">{cfg ? `${cfg.capacite_personnels_min}–${cfg.capacite_personnels_max} personnes` : (v.capacite ? `${v.capacite} personnes` : '—')}</dd>
          <dt>Valeur</dt><dd className="mono">{cfg ? `${cfg.prix.toLocaleString('fr-FR')} €` : '—'}</dd>
          <dt>Premium</dt><dd>{v.premium ? 'Oui ★' : 'Non'}</dd>
        </dl>

        <VehiculeCrewSection vehiculeId={Number(v.id)} />

        <p className="bsec">Mouvements</p>
        <div className="brecrute">
          <select value={transferTarget} onChange={(e) => setTransferTarget(e.target.value)}>
            <option value="">{casernesCibles.length ? 'Transférer vers…' : 'Aucune autre caserne'}</option>
            {casernesCibles.map((b) => (
              <option key={String(b.id)} value={String(b.id)}>{b.nom}</option>
            ))}
          </select>
          <button
            className="btn btn--sm btn--ghost"
            type="button"
            disabled={!transferTarget || transferring}
            onClick={() => doTransfer(Number(v.id))}
          >
            {transferring ? 'Transfert…' : 'Transférer'}
          </button>
          <button
            className="btn btn--sm btn--danger"
            type="button"
            onClick={() => {
              if (window.confirm(`Revendre ${v.nom || `l'engin ${v.id}`} ?`)) handleDeleteVehicule(Number(v.id));
            }}
          >
            Revendre
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="bsplit">
      {/* ── Remise ── */}
      <div className="block">
        <div className="block__h">
          Remise <span className="mono muted">{vehicules.length}/{capV} places · {filteredVehicules.length} affiché(s)</span>
        </div>
        <div className="block__b">
          <div className="brecrute">
            <select value={buyConfigId} onChange={(e) => setBuyConfigId(e.target.value)}>
              {vehiculeConfigs.length ? (
                <>
                  <option value="">Choisir un engin…</option>
                  {vehiculeConfigs.map((c) => (
                    <option key={c.id} value={String(c.id)}>{c.nom} · {c.prix.toLocaleString('fr-FR')} €</option>
                  ))}
                </>
              ) : (
                <option value="">Aucun modèle disponible</option>
              )}
            </select>
            <select value={buyQty} onChange={(e) => setBuyQty(Number(e.target.value))} style={{ flex: '0 0 62px' }}>
              {[1, 2, 5].map((n) => <option key={n} value={n}>×{n}</option>)}
            </select>
            <button className="btn btn--sm btn--primary" type="button" disabled={!buyConfigId || isCreating} onClick={doBuy}>
              {isCreating ? 'Achat…' : 'Acheter'}
            </button>
          </div>
          {createError ? <p className="bwarn">⚠ {createError}</p> : null}
          {createSuccess ? <p className="bsub" style={{ color: '#6ee7b7' }}>{createSuccess}</p> : null}

          <div className="toolbar">
            <input type="search" placeholder="Indicatif, type, statut…" value={vehiculeSearchQuery} onChange={(e) => setVehiculeSearchQuery(e.target.value)} autoComplete="off" />
            <select value={vehiculeFilterStatut} onChange={(e) => setVehiculeFilterStatut(e.target.value)}>
              <option value="all">Tous statuts</option>
              <option value="disponible">Disponibles</option>
              <option value="en_mission">En mission</option>
              <option value="en_maintenance">Maintenance</option>
              <option value="panne">En panne</option>
            </select>
          </div>

          {loadingVehicules ? (
            <div className="empty">Chargement des véhicules…</div>
          ) : filteredVehicules.length === 0 ? (
            <div className="empty">{vehicules.length === 0 ? 'Aucun engin en remise.' : 'Aucun engin ne correspond à la recherche.'}</div>
          ) : (
            <table className="table btable">
              <thead>
                <tr>
                  <th>Indicatif</th>
                  <th>Type</th>
                  <th>Statut</th>
                  <th className="ta-r">Interv.</th>
                  <th className="ta-r">Km</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVehicules.map((v) => {
                  const [bg, fg] = scOf(v.statut);
                  return (
                    <tr key={v.id} className={selectedVehicule?.id === v.id ? 'is-sel' : ''} style={{ cursor: 'pointer' }} onClick={() => handleOpenVehiculeDetails(v)}>
                      <td className="mono" style={{ fontWeight: 700 }}>{v.nom || `Engin ${v.id}`}{v.premium ? ' ★' : ''}</td>
                      <td className="muted">{v.type || '—'}</td>
                      <td><span className="pbadge" style={{ background: bg, color: fg }}>{statutLabel(v.statut)}</span></td>
                      <td className="ta-r mono muted">{interventionsParVehicule.get(v.id) || 0}</td>
                      <td className="ta-r mono muted">{Math.round(Number(v.kilometrage || 0)).toLocaleString('fr-FR')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {totalPages > 1 ? (
            <div className="brecrute" style={{ marginTop: 8, alignItems: 'center' }}>
              <span className="bsub" style={{ flex: 1 }}>Page {currentPage} / {totalPages}</span>
              <button className="btn btn--sm btn--ghost" type="button" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>Précédent</button>
              <button className="btn btn--sm btn--ghost" type="button" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>Suivant</button>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Fiche engin ── */}
      <div className="block">
        <div className="block__h">{selectedVehicule ? `Fiche engin — ${selectedVehicule.nom || `Engin ${selectedVehicule.id}`}` : 'Fiche engin'}</div>
        <div className="block__b">
          {selectedVehicule ? ficheEngin(selectedVehicule) : <div className="empty">Sélectionnez un engin dans la remise.</div>}
        </div>
      </div>
    </div>
  );
};

export default BuildingVehiculesTab;

